import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API_BASE } from '@/context/AuthContext';
import AppHeader from '@/components/AppHeader';
import { 
  Building2, 
  LogOut, 
  User, 
  ShieldCheck, 
  UserCheck, 
  UserX, 
  Plus, 
  Users, 
  FileSpreadsheet, 
  Activity, 
  Clock, 
  CheckCircle2, 
  AlertCircle, 
  X, 
  Lock, 
  RefreshCw,
  Search,
  Filter,
  Stethoscope,
  ArrowRight
} from 'lucide-react';
import axios from 'axios';

export default function StaffHomePage() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  // Admin staff management state
  const [activeTab, setActiveTab] = useState('referrals'); // 'referrals' | 'staff_management'
  const [staffUsers, setStaffUsers] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [staffSuccess, setStaffSuccess] = useState('');
  
  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedStaffUser, setSelectedStaffUser] = useState(null);

  // New Staff form state
  const [newStaff, setNewStaff] = useState({
    name: '',
    username: '',
    password: '',
    role: 'staff',
    status: 'active',
  });
  const [isCreatingStaff, setIsCreatingStaff] = useState(false);

  // Reset Password form state
  const [newPassword, setNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);

  // Filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const fetchStaffUsers = async () => {
    if (user?.role !== 'admin') return;
    setLoadingStaff(true);
    setStaffError('');
    try {
      const res = await axios.get(`${API_BASE}/staff`, {
        headers: getAuthHeader()
      });
      setStaffUsers(res.data);
    } catch (err) {
      setStaffError(err.response?.data?.detail || 'Failed to load staff accounts');
    } finally {
      setLoadingStaff(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin' && activeTab === 'staff_management') {
      fetchStaffUsers();
    }
  }, [user, activeTab]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const handleCreateStaff = async (e) => {
    e.preventDefault();
    setStaffError('');
    setStaffSuccess('');
    setIsCreatingStaff(true);
    try {
      await axios.post(`${API_BASE}/staff`, newStaff, {
        headers: getAuthHeader()
      });
      setStaffSuccess(`Staff account '${newStaff.username}' created successfully.`);
      setShowAddModal(false);
      setNewStaff({ name: '', username: '', password: '', role: 'staff', status: 'active' });
      fetchStaffUsers();
    } catch (err) {
      setStaffError(err.response?.data?.detail || 'Failed to create staff member.');
    } finally {
      setIsCreatingStaff(false);
    }
  };

  const handleToggleStatus = async (staffMember) => {
    setStaffError('');
    setStaffSuccess('');
    const newStatus = staffMember.status === 'active' ? 'inactive' : 'active';
    try {
      await axios.patch(
        `${API_BASE}/staff/${staffMember.id}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      setStaffSuccess(`Status for ${staffMember.name} updated to ${newStatus}.`);
      fetchStaffUsers();
    } catch (err) {
      setStaffError(err.response?.data?.detail || 'Failed to update status.');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedStaffUser || !newPassword) return;
    setIsResetting(true);
    setStaffError('');
    setStaffSuccess('');
    try {
      await axios.post(
        `${API_BASE}/staff/${selectedStaffUser.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeader() }
      );
      setStaffSuccess(`Password for ${selectedStaffUser.name} has been reset.`);
      setShowResetModal(false);
      setNewPassword('');
      setSelectedStaffUser(null);
    } catch (err) {
      setStaffError(err.response?.data?.detail || 'Failed to reset password.');
    } finally {
      setIsResetting(false);
    }
  };

  // Filtered staff members
  const filteredStaff = staffUsers.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          s.username.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' ? true : s.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 flex flex-col">
      <AppHeader />

      {/* Main Content Area */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Welcome Banner */}
        <div data-testid="staff-welcome-card" className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-slate-900 to-teal-950/40 border border-slate-800 p-6 sm:p-8 shadow-xl">
          <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-950 border border-emerald-800 text-emerald-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  Active Authenticated Session
                </span>
                <span className="text-xs text-slate-400">ID: {user?.id?.slice(0, 8)}...</span>
              </div>
              <h1 data-testid="staff-home-title" className="text-2xl sm:text-3xl font-bold tracking-tight text-white">
                Welcome back, {user?.name}!
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                You are logged in as <span className="text-teal-400 font-semibold">{
                  user?.role === 'admin' ? 'System Administrator'
                  : user?.role === 'accountant' ? 'Accountant'
                  : user?.role === 'reception' ? 'Reception'
                  : user?.role === 'lab_staff' ? 'Lab Staff'
                  : user?.role === 'pharmacist' ? 'Pharmacist'
                  : user?.role === 'management' ? 'Management (Read-Only)'
                  : 'Staff Member'
                }</span> with active database access.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <div className="bg-slate-950/60 border border-slate-800/80 px-4 py-3 rounded-xl flex items-center gap-3">
                <Clock className="w-5 h-5 text-teal-400 shrink-0" />
                <div className="text-xs">
                  <div className="text-slate-400">Last Login</div>
                  <div className="text-white font-medium">
                    {user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : 'First Session'}
                  </div>
                </div>
              </div>

              <div className="bg-slate-950/60 border border-slate-800/80 px-4 py-3 rounded-xl flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                <div className="text-xs">
                  <div className="text-slate-400">Multi-User System</div>
                  <div className="text-emerald-300 font-medium">Concurrent Ready</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Navigation Tabs */}
        {user?.role === 'admin' && (
          <div className="flex items-center gap-2 border-b border-slate-800 pb-3">
            <button
              onClick={() => setActiveTab('referrals')}
              data-testid="tab-referrals-btn"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'referrals'
                  ? 'bg-teal-500 text-slate-950 font-semibold shadow-md shadow-teal-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900'
              }`}
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span>Staff Home / Referral Module</span>
            </button>
            <button
              onClick={() => setActiveTab('staff_management')}
              data-testid="tab-staff-management-btn"
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                activeTab === 'staff_management'
                  ? 'bg-teal-500 text-slate-950 font-semibold shadow-md shadow-teal-500/20'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Staff User Management</span>
            </button>
          </div>
        )}

        {/* Tab 1: Staff Home / Referral Entry Placeholder (Foundation) */}
        {activeTab === 'referrals' && (
          <div className="space-y-6">
            <div data-testid="referral-module-placeholder" className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center space-y-4">
              <div className="w-16 h-16 rounded-2xl bg-teal-500/10 border border-teal-500/20 text-teal-400 flex items-center justify-center mx-auto">
                <FileSpreadsheet className="w-8 h-8" />
              </div>
              <h2 className="text-xl font-bold text-white">
                {user?.role === 'accountant' ? 'Accountant Payment Workspace'
                  : user?.role === 'lab_staff' ? 'Lab Staff Workspace'
                  : user?.role === 'pharmacist' ? 'Pharmacist Workspace'
                  : user?.role === 'reception' ? 'Reception Workspace'
                  : user?.role === 'management' ? 'Management Read-Only Workspace'
                  : 'Staff Home & Referral Entry Workspace'}
              </h2>
              <p className="text-slate-400 text-sm max-w-xl mx-auto leading-relaxed">
                {user?.role === 'accountant'
                  ? 'Find any referral by patient, ID, or doctor and record the actual payment against it.'
                  : user?.role === 'lab_staff'
                  ? 'Open an existing referral to enter Lab totals, kit, and percentage or goodwill amount for your section.'
                  : user?.role === 'pharmacist'
                  ? 'Open an existing referral to enter Pharmacy totals, kit, and percentage or goodwill amount for your section.'
                  : user?.role === 'reception'
                  ? 'Capture patient info, referral doctor/marketing exec/consultant, plus Scan and IP amounts. Lab and Pharmacy figures are added later by the concerned staff.'
                  : user?.role === 'management'
                  ? 'View referrals, doctor revenue, and payment status. Download Excel reports. No editing.'
                  : 'Capture a new referral in one place — patient info, referral doctor, marketing executive, consultant, and financials with live calculations.'}
              </p>
              <button
                onClick={() => {
                  if (user?.role === 'accountant') navigate('/staff/referrals?payment_status=pending');
                  else if (user?.role === 'lab_staff' || user?.role === 'pharmacist') navigate('/staff/referrals');
                  else if (user?.role === 'management') navigate('/admin/reports');
                  else navigate('/staff/referrals/new');
                }}
                data-testid="cta-new-referral-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm rounded-xl shadow-md shadow-teal-500/20 transition-all"
              >
                <Plus className="w-4 h-4 stroke-[3]" />
                {user?.role === 'accountant' ? 'Open Pending Payments'
                  : user?.role === 'lab_staff' ? 'Open Referral List (Add Lab Entries)'
                  : user?.role === 'pharmacist' ? 'Open Referral List (Add Pharmacy Entries)'
                  : user?.role === 'management' ? 'Open Reports Dashboard'
                  : 'Enter New Referral'}
              </button>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-teal-400" />
                <span>Staff Login Module Verified (Admin + Multi-Staff Concurrent Access)</span>
              </div>
            </div>

            {/* Quick Staff Information Card */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-xl space-y-1">
                <div className="text-xs text-slate-400">Logged-in Account</div>
                <div className="text-base font-semibold text-white">{user?.name}</div>
                <div className="text-xs text-teal-400 font-mono">@{user?.username}</div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-xl space-y-1">
                <div className="text-xs text-slate-400">Assigned Privilege</div>
                <div className="text-base font-semibold text-white capitalize">{user?.role} Role</div>
                <div className="text-xs text-slate-400">
                  {user?.role === 'admin' && 'Full system access — manage users, doctors, executives, referrals, and payments.'}
                  {user?.role === 'accountant' && 'View referrals, record payments, view revenue reports.'}
                  {user?.role === 'reception' && 'Create/edit referrals with patient info, Scan and IP amounts. Lab and Pharmacy sections are handled by concerned staff.'}
                  {user?.role === 'lab_staff' && 'Edit the Lab section on existing referrals (percentage or goodwill).'}
                  {user?.role === 'pharmacist' && 'Edit the Pharmacy section on existing referrals (percentage or goodwill).'}
                  {user?.role === 'management' && 'Read-only access to referrals, doctor revenue and Excel reports.'}
                  {user?.role === 'staff' && 'Create new referrals and view the referrals list.'}
                </div>
              </div>
              <div className="bg-slate-900/60 border border-slate-800 p-5 rounded-xl space-y-1">
                <div className="text-xs text-slate-400">Account Security Status</div>
                <div className="text-base font-semibold text-emerald-400 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400"></span> Active & Verified
                </div>
                <div className="text-xs text-slate-400">BCrypt Hashed in Central MongoDB</div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Admin Staff User Management (Required for initial setup & staff administration) */}
        {user?.role === 'admin' && activeTab === 'staff_management' && (
          <div className="space-y-6">
            {/* Action Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Staff Accounts Directory</h2>
                <p className="text-xs text-slate-400">Manage multi-user accounts, activate/deactivate access, and reset credentials.</p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={fetchStaffUsers}
                  data-testid="refresh-staff-list-btn"
                  className="p-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 rounded-xl transition-colors"
                  title="Refresh staff list"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingStaff ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowAddModal(true)}
                  data-testid="open-add-staff-modal-btn"
                  className="px-4 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl text-sm transition-all flex items-center gap-2 shadow-md shadow-teal-500/20"
                >
                  <Plus className="w-4 h-4 stroke-[3]" />
                  <span>Create Staff Account</span>
                </button>
              </div>
            </div>

            {/* Notifications */}
            {staffSuccess && (
              <div data-testid="staff-action-success" className="p-4 bg-emerald-950/60 border border-emerald-800/80 rounded-xl text-emerald-200 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{staffSuccess}</span>
              </div>
            )}
            {staffError && (
              <div data-testid="staff-action-error" className="p-4 bg-red-950/60 border border-red-800/80 rounded-xl text-red-200 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                <span>{staffError}</span>
              </div>
            )}

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  placeholder="Search staff by name or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-sm text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                />
              </div>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="bg-slate-900 border border-slate-800 px-4 py-2.5 rounded-xl text-sm text-slate-200 focus:outline-none focus:border-teal-500"
              >
                <option value="all">All Roles</option>
                <option value="staff">Staff Only</option>
                <option value="admin">Admin Only</option>
                <option value="accountant">Accountant Only</option>
                <option value="reception">Reception Only</option>
                <option value="lab_staff">Lab Staff Only</option>
                <option value="pharmacist">Pharmacist Only</option>
                <option value="management">Management Only</option>
              </select>
            </div>

            {/* Staff Users Table */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
              <div className="overflow-x-auto">
                <table data-testid="staff-users-table" className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-950/80 border-b border-slate-800 text-[11px] uppercase tracking-wider text-slate-400">
                      <th className="py-3.5 px-4 font-semibold">Staff Member</th>
                      <th className="py-3.5 px-4 font-semibold">Username</th>
                      <th className="py-3.5 px-4 font-semibold">Role</th>
                      <th className="py-3.5 px-4 font-semibold">Status</th>
                      <th className="py-3.5 px-4 font-semibold">Last Login</th>
                      <th className="py-3.5 px-4 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800/60 text-sm">
                    {filteredStaff.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="py-8 text-center text-slate-500">
                          {loadingStaff ? 'Loading staff records...' : 'No staff accounts found.'}
                        </td>
                      </tr>
                    ) : (
                      filteredStaff.map((staff) => (
                        <tr key={staff.id} className="hover:bg-slate-800/30 transition-colors">
                          <td className="py-4 px-4 font-medium text-white flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center text-slate-300 font-bold text-xs">
                              {staff.name.charAt(0)}
                            </div>
                            <div>
                              <div>{staff.name}</div>
                              <div className="text-[11px] text-slate-500 font-mono">ID: {staff.id.slice(0, 8)}...</div>
                            </div>
                          </td>
                          <td className="py-4 px-4 font-mono text-slate-300">
                            @{staff.username}
                          </td>
                          <td className="py-4 px-4">
                            <span className={`text-xs px-2.5 py-1 rounded-md font-medium uppercase tracking-wider ${
                              staff.role === 'admin'
                                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                : staff.role === 'accountant'
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                : staff.role === 'reception'
                                ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30'
                                : staff.role === 'lab_staff'
                                ? 'bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/30'
                                : staff.role === 'pharmacist'
                                ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                                : staff.role === 'management'
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                : 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                            }`}>
                              {staff.role}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${
                              staff.status === 'active'
                                ? 'bg-emerald-950/80 text-emerald-300 border border-emerald-800/80'
                                : 'bg-rose-950/80 text-rose-300 border border-rose-800/80'
                            }`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${staff.status === 'active' ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                              {staff.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-xs text-slate-400">
                            {staff.last_login_at ? new Date(staff.last_login_at).toLocaleString() : 'Never'}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {/* Toggle Active/Inactive */}
                              <button
                                onClick={() => handleToggleStatus(staff)}
                                data-testid={`toggle-status-btn-${staff.username}`}
                                className={`px-2.5 py-1.5 text-xs rounded-lg border transition-colors ${
                                  staff.status === 'active'
                                    ? 'bg-rose-950/40 text-rose-300 border-rose-800/60 hover:bg-rose-900/60'
                                    : 'bg-emerald-950/40 text-emerald-300 border-emerald-800/60 hover:bg-emerald-900/60'
                                }`}
                                title={staff.status === 'active' ? 'Deactivate account' : 'Activate account'}
                              >
                                {staff.status === 'active' ? 'Deactivate' : 'Activate'}
                              </button>

                              {/* Reset Password */}
                              <button
                                onClick={() => {
                                  setSelectedStaffUser(staff);
                                  setShowResetModal(true);
                                }}
                                data-testid={`reset-pwd-btn-${staff.username}`}
                                className="p-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-lg transition-colors"
                                title="Reset Password"
                              >
                                <Lock className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Staff Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl p-6 shadow-2xl animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-teal-400" />
                Create New Staff Account
              </h3>
              <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateStaff} className="space-y-4 mt-4">
              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Staff Member Name
                </label>
                <input
                  data-testid="new-staff-name-input"
                  type="text"
                  required
                  placeholder="e.g. John Doe"
                  value={newStaff.name}
                  onChange={(e) => setNewStaff({ ...newStaff, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Username
                </label>
                <input
                  data-testid="new-staff-username-input"
                  type="text"
                  required
                  placeholder="e.g. john.doe"
                  value={newStaff.username}
                  onChange={(e) => setNewStaff({ ...newStaff, username: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  Initial Password
                </label>
                <input
                  data-testid="new-staff-password-input"
                  type="password"
                  required
                  placeholder="Minimum 6 characters"
                  value={newStaff.password}
                  onChange={(e) => setNewStaff({ ...newStaff, password: e.target.value })}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                    Role
                  </label>
                  <select
                    data-testid="new-staff-role-select"
                    value={newStaff.role}
                    onChange={(e) => setNewStaff({ ...newStaff, role: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                    <option value="accountant">Accountant</option>
                    <option value="reception">Reception</option>
                    <option value="lab_staff">Lab Staff</option>
                    <option value="pharmacist">Pharmacist</option>
                    <option value="management">Management (Read-Only)</option>
                    <option value="manager">Manager (Referral Calculations)</option>
                    <option value="marketing_executive">Marketing Executive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                    Status
                  </label>
                  <select
                    data-testid="new-staff-status-select"
                    value={newStaff.status}
                    onChange={(e) => setNewStaff({ ...newStaff, status: e.target.value })}
                    className="w-full px-3 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {newStaff.role === 'marketing_executive' && (
                <div className="text-[11px] text-slate-500 bg-slate-950/60 border border-slate-800 rounded-lg px-3 py-2">
                  This account will appear in the Marketing Executive dropdown when a referral is created. It will only see referrals assigned to itself.
                </div>
              )}

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-create-staff-btn"
                  disabled={isCreatingStaff}
                  className="px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-xl text-sm transition-all shadow-md shadow-teal-500/20 disabled:opacity-50"
                >
                  {isCreatingStaff ? 'Creating...' : 'Save Staff Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && selectedStaffUser && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between pb-4 border-b border-slate-800">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Lock className="w-5 h-5 text-amber-400" />
                Reset Password
              </h3>
              <button onClick={() => setShowResetModal(false)} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
              <p className="text-xs text-slate-400">
                Set a new password for <span className="text-white font-semibold">{selectedStaffUser.name}</span> (@{selectedStaffUser.username}).
              </p>

              <div>
                <label className="block text-xs font-semibold text-slate-300 uppercase tracking-wider mb-1">
                  New Password
                </label>
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-sm text-white focus:outline-none focus:border-teal-500"
                />
              </div>

              <div className="pt-4 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowResetModal(false)}
                  className="px-4 py-2 text-sm text-slate-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-reset-password-btn"
                  disabled={isResetting}
                  className="px-5 py-2.5 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-xl text-sm transition-all shadow-md shadow-amber-500/20 disabled:opacity-50"
                >
                  {isResetting ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
